/**
 * FormularioMovimiento - Formulario para editar datos contables de un movimiento
 * Permite asignar: Cuenta (obligatorio), Centro O Entidad (opcional), Clasificador (opcional)
 */
import React, { useState, useEffect } from 'react';
import {
  Paper, Box, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem,
  ToggleButtonGroup, ToggleButton, Alert, CircularProgress, Grid
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccountTree as AccountTreeIcon,
  Business as BusinessIcon,
  DoNotDisturb as DoNotDisturbIcon
} from '@mui/icons-material';
import { formatearFecha, formatearMonto } from '../../utils/formatters';

const FormularioMovimiento = ({
  movimiento = null,
  cuentas = [],
  centros = [],
  clasificadores = [],
  entidades = [],
  loading = false,
  error = null,
  onGuardar = () => {},
  onCancelar = () => {}
}) => {
  const [tipoAsignacion, setTipoAsignacion] = useState('centro'); // 'centro', 'entidad' o 'ninguno'
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState('');
  const [centroSeleccionado, setCentroSeleccionado] = useState('');
  const [clasificadorSeleccionado, setClasificadorSeleccionado] = useState('');
  const [entidadSeleccionada, setEntidadSeleccionada] = useState('');
  const [glosa, setGlosa] = useState('');
  const [referencia, setReferencia] = useState('');
  const [validacionError, setValidacionError] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Helper: validar que un ID existe en las opciones disponibles
  const validarIdEnOpciones = (id, opciones) => {
    if (!id) return '';
    const idStr = id.toString();
    return opciones.some(o => o.id.toString() === idStr) ? idStr : '';
  };

  // Inicializar formulario cuando hay movimiento seleccionado
  useEffect(() => {
    if (!movimiento) return;

    setCuentaSeleccionada(validarIdEnOpciones(movimiento.cuenta_id, cuentas));
    setCentroSeleccionado(validarIdEnOpciones(movimiento.centro_id, centros));
    setClasificadorSeleccionado(validarIdEnOpciones(movimiento.clasificador_id, clasificadores));
    setEntidadSeleccionada(validarIdEnOpciones(movimiento.entidad_id, entidades));
    setGlosa(movimiento.glosa || '');
    setReferencia(movimiento.referencia || '');
    setValidacionError('');

    // Determinar tipo de asignación
    if (movimiento.entidad_id) {
      setTipoAsignacion('entidad');
    } else if (movimiento.centro_id) {
      setTipoAsignacion('centro');
    } else {
      // Si no tiene ni centro ni entidad, es "no aplica"
      setTipoAsignacion('ninguno');
    }
  }, [movimiento, cuentas, centros, clasificadores, entidades]);

  // Validar y guardar
  const handleGuardar = async () => {
    setValidacionError('');

    // Validar cuenta (obligatoria)
    if (!cuentaSeleccionada) {
      setValidacionError('Debe seleccionar una cuenta contable');
      return;
    }

    // Validar según tipo de asignación
    if (tipoAsignacion === 'centro' && !centroSeleccionado) {
      setValidacionError('Debe seleccionar un centro de costo');
      return;
    }

    if (tipoAsignacion === 'entidad' && !entidadSeleccionada) {
      setValidacionError('Debe seleccionar una entidad');
      return;
    }

    // Preparar datos para guardar
    // Lógica de prioridad: Centro > Entidad > Ninguno
    const datosActualizar = {
      cuenta_id: parseInt(cuentaSeleccionada),
      centro_id: tipoAsignacion === 'centro' ? (centroSeleccionado ? parseInt(centroSeleccionado) : null) : null,
      clasificador_id: tipoAsignacion === 'centro' ? (clasificadorSeleccionado ? parseInt(clasificadorSeleccionado) : null) : null,
      entidad_id: tipoAsignacion === 'entidad' ? (entidadSeleccionada ? parseInt(entidadSeleccionada) : null) : null,
      glosa: glosa || movimiento.glosa,
      // Referencia: Siempre guardar, con o sin tipo de asignación
      // Si tiene valor (cadena no vacía), guardar el valor
      // Si está vacío, guardar como null
      referencia: referencia.trim() || null
    };

    setGuardando(true);
    try {
      await onGuardar(movimiento.id, datosActualizar);
    } catch (err) {
      setValidacionError(err.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  // Cancelar edición
  const handleCancelar = () => {
    onCancelar();
  };

  if (!movimiento) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', color: 'textSecondary' }}>
        <Typography>Selecciona un movimiento para editar</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, pt: 3, height: '600px', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>
        {movimiento.estado === 'PENDIENTE' ? 'Completar Movimiento' : 'Modificar Movimiento'}
      </Typography>

      {/* Información del movimiento (solo lectura) */}
      <Box sx={{ mb: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Fecha</Typography>
            <Typography variant="body2">{formatearFecha(movimiento.fecha)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Folio</Typography>
            <Typography variant="body2">{movimiento.folio}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Banco</Typography>
            <Typography variant="body2">{movimiento.cuenta_banco}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Ingreso</Typography>
            <Typography variant="body2">
              {movimiento.ingreso > 0 ? formatearMonto(movimiento.ingreso) : '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Egreso</Typography>
            <Typography variant="body2">
              {movimiento.egreso > 0 ? formatearMonto(movimiento.egreso) : '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary">Glosa Original</Typography>
            <Typography variant="body2">{movimiento.glosa}</Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Errores */}
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>
          {error}
        </Alert>
      )}

      {validacionError && (
        <Alert severity="warning" sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>
          {validacionError}
        </Alert>
      )}

      {/* Formulario */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: 1.5, flex: 1, overflow: 'auto' }}>
        {/* Cuenta (obligatorio) */}
        <FormControl fullWidth required sx={{ mt: 1 }}>
          <InputLabel>Cuenta Contable</InputLabel>
          <Select
            value={cuentaSeleccionada}
            label="Cuenta Contable"
            onChange={(e) => setCuentaSeleccionada(e.target.value)}
            disabled={guardando}
          >
            <MenuItem value="">-- Seleccione Cuenta --</MenuItem>
            {cuentas.map((c) => (
              <MenuItem key={c.id} value={c.id.toString()}>
                {c.codigo} - {c.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Tipo de asignación (Centro vs Entidad) */}
        <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 0.75 }}>
          <Typography variant="caption" sx={{ mb: 0.3, display: 'block', fontSize: '0.75rem' }}>
            ¿A qué asociar?
          </Typography>
          <ToggleButtonGroup
            value={tipoAsignacion}
            exclusive
            onChange={(e, newValue) => {
              if (newValue) {
                setTipoAsignacion(newValue);
                if (newValue === 'centro') {
                  setEntidadSeleccionada('');
                  setClasificadorSeleccionado('');
                  setReferencia('');
                } else if (newValue === 'entidad') {
                  setCentroSeleccionado('');
                  setClasificadorSeleccionado('');
                } else if (newValue === 'ninguno') {
                  // Limpiar todo para "No aplica"
                  setCentroSeleccionado('');
                  setClasificadorSeleccionado('');
                  setEntidadSeleccionada('');
                  setReferencia('');
                }
              }
            }}
            fullWidth
            disabled={guardando}
            size="small"
          >
            <ToggleButton value="centro" sx={{ fontSize: '0.85rem', py: 0.5 }}>
              <AccountTreeIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Centro
            </ToggleButton>
            <ToggleButton value="entidad" sx={{ fontSize: '0.85rem', py: 0.5 }}>
              <BusinessIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Entidad
            </ToggleButton>
            <ToggleButton value="ninguno" sx={{ fontSize: '0.85rem', py: 0.5 }}>
              <DoNotDisturbIcon sx={{ mr: 0.5, fontSize: 16 }} />
              No aplica
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Centro (si selecciona Centro) */}
        {tipoAsignacion === 'centro' && (
          <>
            <FormControl fullWidth required>
              <InputLabel>Centro de Costo</InputLabel>
              <Select
                value={centroSeleccionado}
                label="Centro de Costo"
                onChange={(e) => setCentroSeleccionado(e.target.value)}
                disabled={guardando}
              >
                <MenuItem value="">-- Seleccione Centro --</MenuItem>
                {centros.map((c) => (
                  <MenuItem key={c.id} value={c.id.toString()}>
                    {c.codigo} - {c.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clasificador (opcional con Centro) */}
            <FormControl fullWidth>
              <InputLabel>Clasificador (Opcional)</InputLabel>
              <Select
                value={clasificadorSeleccionado}
                label="Clasificador (Opcional)"
                onChange={(e) => setClasificadorSeleccionado(e.target.value)}
                disabled={guardando}
              >
                <MenuItem value="">-- Sin Clasificador --</MenuItem>
                {clasificadores.map((c) => (
                  <MenuItem key={c.id} value={c.id.toString()}>
                    {c.codigo} - {c.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {/* Entidad (si selecciona Entidad) */}
        {tipoAsignacion === 'entidad' && (
          <FormControl fullWidth required>
            <InputLabel>Entidad</InputLabel>
            <Select
              value={entidadSeleccionada}
              label="Entidad"
              onChange={(e) => setEntidadSeleccionada(e.target.value)}
              disabled={guardando}
            >
              <MenuItem value="">-- Seleccione Entidad --</MenuItem>
              {entidades.map((e) => (
                <MenuItem key={e.id} value={e.id.toString()}>
                  {e.rut} - {e.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Glosa (opcional) */}
        <TextField
          label="Glosa (Opcional)"
          multiline
          rows={1}
          size="small"
          value={glosa}
          onChange={(e) => setGlosa(e.target.value)}
          placeholder="Descripción del movimiento"
          disabled={guardando}
        />

        {/* Referencia (opcional) */}
        <TextField
          label="Referencia (Opcional)"
          size="small"
          value={String(referencia).replace(/\.0$/, '')}
          onChange={(e) => setReferencia(e.target.value)}
          placeholder="Ej: FAC-123, OC-456"
          disabled={guardando}
        />
      </Box>

      {/* Botones */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 'auto', pt: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CancelIcon />}
          onClick={handleCancelar}
          disabled={guardando}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={guardando ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleGuardar}
          disabled={guardando}
        >
          {guardando ? 'Guardando...' : 'Guardar'}
        </Button>
      </Box>
    </Paper>
  );
};

export default FormularioMovimiento;
